/**
 * LanguageIconButton — Globe icon shared across all 4 portal headers.
 *
 * - Desktop: small 38x38 icon button in topbar with Tooltip on hover.
 * - Mobile: small 32x32 icon button in mobile header.
 * - On tap: opens a BottomSheet with the 11-language LanguageSelector.
 * - Persistence (ADMINREPORTS-1, 2026-07-17): localStorage for instant/offline UI, PLUS
 *   `PATCH /language/preference` so the choice follows the account to any device. That is the real,
 *   already-wired endpoint (LanguageSelectorPage has used it since 2026-06-21) — the old TODO here
 *   pointed at `/users/me/preferences`, which does not exist.
 *
 * Save to: client/src/components/common/LanguageIconButton.tsx
 */
import { useState, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../ui/BottomSheet';
import { Tooltip } from '../ui/tooltip';
import { LanguageSelector, LANGUAGES } from './LanguageSelector';
import { changeAppLanguage } from '../../i18n';

interface LanguageIconButtonProps {
  /** 'desktop' (38px icon) / 'mobile' (32px icon) / 'pill' (labeled globe + native + chevron, LOGIN-POLISH-2). */
  variant?: 'desktop' | 'mobile' | 'pill';
  /** For 'pill' on a dark surface (the mobile purple banner) — light-on-dark styling. Default 'light'. */
  tone?: 'light' | 'onDark';
  /** LANG-2: fired AFTER the language is applied + synced (an authenticated in-portal switch). The
   *  student profile uses it to update the cached User (preferredLanguage + languageChosenAt) so a
   *  reload doesn't re-adopt. Omitted pre-login (no user yet). */
  onChanged?: (code: string) => void;
}

export function LanguageIconButton({
  variant = 'desktop',
  tone = 'light',
  onChanged,
}: LanguageIconButtonProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<string>(i18n.language);

  // Keep the picker's pending selection in sync with the live app language.
  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language]);

  /**
   * ADMINREPORTS-1 (S item) — persist the choice server-side, not just in localStorage.
   *
   * The old TODO guessed a path that doesn't exist (`/users/me/preferences`); the real, already-wired
   * endpoint is `PATCH /language/preference` (see LanguageSelectorPage — the page-level selector has
   * used it since 2026-06-21). Without this, picking a language from the shell globe survived only
   * on that one device and was lost on any other, while the login-time selector remembered it — the
   * two surfaces silently disagreed.
   *
   * localStorage is still written FIRST so the UI switches instantly and still works offline; the
   * PATCH is best-effort. A failure is surfaced but never reverts the local choice — the user's
   * pick is honoured on this device either way.
   */
  /**
   * Apply the selected language: switch the UI immediately, persist to localStorage,
   * and best-effort sync to the account (PATCH /language/preference) — all via the shared
   * changeAppLanguage helper (I18N-T1). The server sync is best-effort; the local choice
   * is always honoured.
   */
  const handleConfirm = async (code: string): Promise<void> => {
    setLang(code);
    setOpen(false);
    await changeAppLanguage(code, { persistServer: true });
    onChanged?.(code); // LANG-2: let an authenticated caller (student profile) sync the cached User
  };

  const size = variant === 'desktop' ? 38 : 32;
  const iconSize = variant === 'desktop' ? 17 : 15;
  const radius = variant === 'desktop' ? 12 : 8;

  const currentLang = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];

  // LOGIN-POLISH-2: a compact, LABELLED pill — a first-time user can READ that language is changeable
  // ("🌐 English ▾" / "🌐 हिंदी ▾"). Min 44px tap target. `onDark` tone for the mobile purple banner.
  if (variant === 'pill') {
    const onDark = tone === 'onDark';
    const bg = onDark ? 'rgba(255,255,255,0.14)' : 'var(--surface)';
    const bgHover = onDark ? 'rgba(255,255,255,0.24)' : 'var(--p-lt)';
    const bd = onDark ? 'rgba(255,255,255,0.30)' : 'var(--border)';
    const bdHover = onDark ? 'rgba(255,255,255,0.48)' : 'var(--p-md)';
    const fg = onDark ? '#fff' : 'var(--t2)';
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          aria-label={t('common:language.change')}
          data-testid="lang-switcher-pill"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            minHeight: 44, padding: '0 13px', borderRadius: 22,
            background: bg, border: `1.5px solid ${bd}`, color: fg,
            fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)',
            cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = bgHover; e.currentTarget.style.borderColor = bdHover; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = bg; e.currentTarget.style.borderColor = bd; }}
        >
          <Globe size={15} color={fg} />
          <span>{currentLang.native}</span>
          <ChevronDown size={14} color={onDark ? 'rgba(255,255,255,0.85)' : 'var(--t3)'} />
        </button>
        <BottomSheet open={open} onClose={() => setOpen(false)} title={t('common:language.title')}>
          <div style={{ padding: '4px 0 8px' }}>
            <p style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--t3)', marginBottom: 14, fontFamily: 'var(--font-body)' }}>
              {t('common:language.subtitle')}
            </p>
            <LanguageSelector value={lang} onChange={setLang} onConfirm={handleConfirm} />
          </div>
        </BottomSheet>
      </>
    );
  }

  return (
    <>
      <Tooltip text={`${t('common:language.current')}: ${currentLang.native}`} position="bottom">
        <button
          onClick={() => setOpen(true)}
          aria-label={t('common:language.change')}
          style={{
            width: size,
            height: size,
            background: 'var(--surface)',
            border: '1.5px solid var(--border)',
            borderRadius: radius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            position: 'relative',
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--p-lt)';
            e.currentTarget.style.borderColor = 'var(--p-md)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--surface)';
            e.currentTarget.style.borderColor = 'var(--border)';
          }}
        >
          <Globe size={iconSize} color="var(--t2)" />
          {/* Tiny language code badge at bottom-right */}
          <span
            style={{
              position: 'absolute',
              bottom: -3,
              right: -3,
              padding: '1px 4px',
              borderRadius: 6,
              background: 'var(--p)',
              color: '#fff',
              fontSize: 8,
              fontWeight: 800,
              fontFamily: 'var(--font-body)',
              letterSpacing: 0,
              lineHeight: 1.2,
              border: '1.5px solid var(--surface)',
              textTransform: 'uppercase',
            }}
          >
            {lang}
          </span>
        </button>
      </Tooltip>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={t('common:language.title')}>
        <div style={{ padding: '4px 0 8px' }}>
          <p style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--t3)', marginBottom: 14, fontFamily: 'var(--font-body)' }}>
            {t('common:language.subtitle')}
          </p>
          <LanguageSelector value={lang} onChange={setLang} onConfirm={handleConfirm} />
        </div>
      </BottomSheet>
    </>
  );
}

export default LanguageIconButton;